import { useDroppable } from '@dnd-kit/core'
import { Ban } from 'lucide-react'
import type { ReactNode } from 'react'

import { cn } from '@/shared/lib/cn'

export interface RejectDropZoneProps {
  id: string
  /** Por qué no se puede soltar acá. Se muestra mientras el item está encima. */
  reason: string
  /**
   * Posición nominal de la zona respecto de la lista. Se publica como
   * `data-dnd-index` igual que los items: es lo que le dice al sensor de teclado
   * que esta zona está DEBAJO de la última fila y no flotando en cualquier lado.
   */
  index?: number
  className?: string
  children?: ReactNode
}

/**
 * Una zona que **registra** el drop para poder rechazarlo.
 *
 * Suena contradictorio, pero es deliberado: si la sección de permanentes no
 * fuera droppable, soltar ahí simplemente no haría nada y la persona no sabría
 * por qué. Registrándola, dnd-kit nos entrega el `over`, el drop se rechaza
 * explícitamente y el motivo aparece escrito mientras el item está encima.
 */
export function RejectDropZone({ id, reason, index, className, children }: RejectDropZoneProps) {
  const { setNodeRef, isOver, active } = useDroppable({ id })
  const showTooltip = isOver && active !== null

  return (
    <div
      ref={setNodeRef}
      data-dnd-index={index}
      data-over={showTooltip || undefined}
      className={cn(
        'relative rounded-[var(--radius-card)]',
        showTooltip && 'ring-1 ring-overdue/60',
        className,
      )}
    >
      {showTooltip ? (
        <div
          role="tooltip"
          className={cn(
            'hairline absolute -top-2 left-3 z-20 flex items-center gap-1.5 rounded-[var(--radius-control)]',
            'bg-surface-2 px-2 py-1 text-2xs text-overdue',
          )}
        >
          <Ban size={12} aria-hidden="true" />
          {reason}
        </div>
      ) : null}

      {children}
    </div>
  )
}
